// Abilities: the active mech's passive, signature power and ultimate, plus the
// ultimate charge economy shared by every mech. The per-mech behaviour lives in
// volt.ts / bastion.ts / wraith.ts behind the Kit interface; this file owns the
// cooldown, charge, input, events, cross-mech synergy flags and lifecycle.

import type Phaser from 'phaser';
import { ULTIMATE_COST } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { AbilityApi, EnemyApi, GameContext } from '../../core/contracts';
import type { EventKey, GameEvents } from '../../core/events';
import type { DamageInfo, PassiveId, SignatureId, UltimateId } from '../../core/types';
import { createBastionKit } from './bastion';
import {
  addChargePoints,
  chargeForDamageDealt,
  chargeForDamageTaken,
  chargeForKill,
  feedbackCost,
  FEEDBACK_COST,
  signatureCooldownSeconds,
  StillTracker,
} from './math';
import { Bolts, ensureAbilityTextures, Safe, Scheduler, sfx, type Kit, type KitHost } from './shared';
import { createVoltKit } from './volt';
import { createWraithKit } from './wraith';

/** WALKING_FORTRESS: still this long → +25% damage dealt, −25% damage taken. */
const WALKING_FORTRESS_STILL = 0.5;
/** A signature press this recent still fires when the cooldown finishes (input buffering). */
const SIGNATURE_BUFFER = 0.12;

export function createAbilities(ctx: GameContext): AbilityApi {
  return new AbilitySystem(ctx);
}

class AbilitySystem implements AbilityApi {
  readonly signatureId: SignatureId;
  readonly ultimateId: UltimateId;
  readonly passiveId: PassiveId;

  private readonly kit: Kit;
  private readonly timers = new Scheduler();
  private readonly safe = new Safe();
  private readonly bolts: Bolts;
  private readonly still = new StillTracker(30);
  private readonly offs: (() => void)[] = [];
  private readonly objects = new Set<Phaser.GameObjects.GameObject>();

  private cooldown = 0;
  private cooldownMax: number;
  /** ultimateReady was announced for the current full bar. */
  private readyAnnounced = false;
  private started = false;
  private destroyed = false;
  private walkingFortressOn = false;

  constructor(private readonly ctx: GameContext) {
    const mech = ctx.run.mech;
    this.signatureId = mech.signature;
    this.ultimateId = mech.ultimate;
    this.passiveId = mech.passive;
    this.cooldownMax = mech.signatureCooldown;
    ensureAbilityTextures(ctx.scene);
    this.bolts = new Bolts(ctx.scene);

    const self = this;
    const host: KitHost = {
      ctx,
      timers: this.timers,
      safe: this.safe,
      bolts: this.bolts,
      flag: (name) => this.flag(name),
      get stillFor() {
        return self.still.stillFor;
      },
      on: <K extends EventKey>(type: K, fn: (p: GameEvents[K]) => void) => {
        const off = ctx.bus.on(type, fn);
        this.offs.push(off);
        return off;
      },
      track: (obj) => {
        this.objects.add(obj);
        return obj;
      },
      untrack: (obj) => {
        this.objects.delete(obj);
      },
    };

    switch (mech.id) {
      case 'bastion':
        this.kit = createBastionKit(host);
        break;
      case 'wraith':
        this.kit = createWraithKit(host);
        break;
      default:
        this.kit = createVoltKit(host);
        break;
    }
  }

  // ───────────── AbilityApi ─────────────

  get signatureCooldown(): number {
    return this.cooldown;
  }

  get signatureCooldownMax(): number {
    return this.cooldownMax;
  }

  get signatureActive(): boolean {
    return this.kit.signatureActive;
  }

  get ultimateActive(): boolean {
    return this.kit.ultimateActive;
  }

  get ultimateReady(): boolean {
    return this.charge >= ULTIMATE_COST;
  }

  outgoingMultiplier(target: EnemyApi | null, info: DamageInfo): number {
    if (info.source !== 'player') return 1;
    let m = this.safe.call('kit.outgoing', () => this.kit.outgoing(target, info), 1);
    if (this.walkingFortressActive()) m *= 1.25;
    return m;
  }

  incomingMultiplier(info: DamageInfo): number {
    // Self-inflicted costs (FEEDBACK_LOOP) are exact.
    if (info.source === 'self') return 1;
    let m = this.safe.call('kit.incoming', () => this.kit.incoming(info), 1);
    if (this.walkingFortressActive()) m *= 0.75;
    return m;
  }

  addCharge(points: number): void {
    // No charge while the ultimate is running: it cannot be chained back to back.
    if (this.destroyed || this.kit.ultimateActive || !(points > 0)) return;
    const p = this.ctx.player;
    const mult = this.safe.call('player.stats', () => p.stats.ultimateCharge, 1);
    p.ultCharge = addChargePoints(p.ultCharge, points, mult, ULTIMATE_COST);
    this.checkUltimateReady();
  }

  reduceSignatureCooldown(seconds: number): void {
    if (!(seconds > 0) || this.cooldown <= 0) return;
    this.setCooldown(this.cooldown - seconds);
  }

  // ───────────── lifecycle ─────────────

  start(): void {
    if (this.started) return;
    this.started = true;
    // A bar that is already full when the room starts was announced in the previous room.
    this.readyAnnounced = this.charge >= ULTIMATE_COST;

    this.on('enemy:damaged', (e) => this.addCharge(chargeForDamageDealt(e.amount, e.info)));
    this.on('enemy:killed', (e) => this.addCharge(chargeForKill(e.enemy.kind, e.enemy.elite.length > 0, e.info.source)));
    this.on('player:damaged', (e) => this.addCharge(chargeForDamageTaken(e.amount, e.info.source)));
    this.on('player:died', () => this.safe.run('kit.interrupt', () => this.kit.interrupt()));
    this.on('room:exitRequested', () => this.safe.run('kit.interrupt', () => this.kit.interrupt()));

    this.safe.run('kit.start', () => this.kit.start());
  }

  update(dt: number): void {
    if (this.destroyed) return;
    const ctx = this.ctx;
    const p = ctx.player;

    if (this.cooldown > 0) this.setCooldown(this.cooldown - dt);

    const alive = this.safe.call('player.alive', () => p.alive, false);
    this.safe.run('player.still', () => this.still.update(dt, p.vx, p.onGround, alive));
    this.updateWalkingFortress();

    if (alive && this.canAct()) {
      const input = ctx.input;
      // Signature: a fresh press, or one buffered just before the cooldown finished.
      if (this.cooldown <= 0 && (input.pressed('signature') || input.sincePressed('signature') <= SIGNATURE_BUFFER)) {
        input.consume('signature');
        this.useSignature();
      }
      if (input.pressed('ultimate') && this.ultimateReady && !this.kit.ultimateActive) this.useUltimate();
    }

    this.timers.update(dt);
    this.safe.run('kit.update', () => this.kit.update(dt));
    this.bolts.update(dt);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const off of this.offs) off();
    this.offs.length = 0;
    this.timers.clear();
    try {
      this.kit.destroy();
    } catch (err) {
      console.error('[abilities] kit destroy failed', err);
    }
    this.bolts.destroy();
    for (const obj of this.objects) {
      try {
        obj.destroy();
      } catch {
        // Already destroyed by the scene shutdown.
      }
    }
    this.objects.clear();
  }

  // ───────────── internals ─────────────

  private get charge(): number {
    return this.ctx.player?.ultCharge ?? 0;
  }

  private on<K extends EventKey>(type: K, fn: (p: GameEvents[K]) => void): void {
    this.offs.push(this.ctx.bus.on(type, fn));
  }

  private flag(name: string): boolean {
    return this.safe.call('effects.hasFlag', () => this.ctx.effects.hasFlag(name), false);
  }

  /** Control is enabled (no menu, not locked by a cutscene / room transition). */
  private canAct(): boolean {
    const ctx = this.ctx;
    if (!ctx.input.enabled) return false;
    const state = this.safe.call('player.state', () => ctx.player.state, 'locked' as const);
    return state !== 'locked' && state !== 'dead';
  }

  private setCooldown(v: number): void {
    const was = this.cooldown;
    this.cooldown = Math.max(0, v);
    if (was > 0 && this.cooldown <= 0) {
      this.ctx.bus.emit('player:signatureReady', { id: this.signatureId });
      sfx(this.ctx, this.safe, 'signature_ready', { volume: 0.7 });
      const p = this.ctx.player;
      this.safe.run('fx.ring', () =>
        this.ctx.fx.ring(p.x, p.y, 70, { color: this.ctx.run.mech.glowColor, width: 3, fromRadius: 30 }),
      );
    }
  }

  private useSignature(): void {
    const ctx = this.ctx;
    if (!this.safe.call('kit.canSignature', () => this.kit.canSignature(), false)) return;
    const p = ctx.player;
    const statMult = this.safe.call('player.stats', () => p.stats.signatureCooldown, 1);
    this.cooldownMax = signatureCooldownSeconds(ctx.run.mech.signatureCooldown, statMult);
    this.cooldown = this.cooldownMax;

    this.safe.run('kit.signature', () => this.kit.signature());

    // FEEDBACK_LOOP: every use burns 8 HP (never below 1 HP).
    if (this.flag('FEEDBACK_LOOP') && feedbackCost(p.hp) > 0) {
      this.safe.run('damage.hitPlayer', () =>
        ctx.damage.hitPlayer({ amount: FEEDBACK_COST, source: 'self', ignoreIFrames: true, tags: [], label: 'feedback' }),
      );
    }
    ctx.bus.emit('player:signature', { id: this.signatureId });
  }

  private useUltimate(): void {
    const ctx = this.ctx;
    ctx.player.ultCharge = 0;
    this.readyAnnounced = false;
    this.safe.run('kit.ultimate', () => this.kit.ultimate());
    ctx.bus.emit('player:ultimate', { id: this.ultimateId });
  }

  private checkUltimateReady(): void {
    if (this.charge >= ULTIMATE_COST) {
      if (!this.readyAnnounced) {
        this.readyAnnounced = true;
        this.ctx.bus.emit('player:ultimateReady', {});
        sfx(this.ctx, this.safe, 'ultimate_ready');
        const p = this.ctx.player;
        this.safe.run('fx.ring', () => this.ctx.fx.ring(p.x, p.y, 110, { color: COLORS.ultBar, width: 4, fromRadius: 40 }));
      }
    } else {
      this.readyAnnounced = false;
    }
  }

  private walkingFortressActive(): boolean {
    return this.still.isStill(WALKING_FORTRESS_STILL) && this.flag('WALKING_FORTRESS');
  }

  /** Small cue when WALKING_FORTRESS engages so the player learns the stance. */
  private updateWalkingFortress(): void {
    const on = this.walkingFortressActive();
    if (on && !this.walkingFortressOn) {
      const p = this.ctx.player;
      this.safe.run('fx.ring', () => this.ctx.fx.ring(p.x, p.feetY - 4, 64, { color: COLORS.shield, width: 2, fromRadius: 24 }));
    }
    this.walkingFortressOn = on;
  }
}
